import React, { useCallback } from 'react';
import { FieldErrors } from 'react-hook-form';
import * as yup from 'yup';

import { useLogin } from 'api';
import useAuthForm from 'ui/hooks/useAuthForm';
import { AuthSuccessResponse } from 'ui/types';
import { ensureAuthResponse } from 'ui/utils';

import { GENERIC_ERROR_MESSAGE } from './constants';
import { getLoginFormError } from './utils';

export interface UseLoginFormConfig {
  onAuthenticateSuccess?: (result: AuthSuccessResponse) => void;
  onError?: (errors: FieldErrors<LoginFormData> | Error) => void;
  onSubmit?: () => void;
}

export interface LoginFormData {
  email: string;
  password: string;
}

type UseLoginFormResult = Pick<
  ReturnType<typeof useAuthForm<LoginFormData>>,
  'formState' | 'register' | 'validationState'
> & {
  hasInputError: boolean;
  isLoading: boolean;
  onSubmit: (e: React.SyntheticEvent) => Promise<void>;
};

const schema = yup.object().shape({
  email: yup.string().required(),
  password: yup.string().required(),
});

export default function useLoginForm(
  config?: UseLoginFormConfig,
): UseLoginFormResult {
  const { onAuthenticateSuccess, onError, onSubmit } = config ?? {};

  const { mutate, isLoading } = useLogin();

  const {
    clearErrors,
    formState,
    handleSubmit,
    hasInputError,
    register,
    setError,
    validationState,
  } = useAuthForm<LoginFormData>({
    schema,
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const submitForm = useCallback(
    async (data: Omit<LoginFormData, 'unregisteredError'>) => {
      const { email, password } = data;

      clearErrors('unregisteredError');

      onSubmit?.();

      mutate(
        { email, password },
        {
          onError(err) {
            const message = getLoginFormError(err);
            setError('unregisteredError', { message });
            onError?.(new Error(message));
          },
          onSuccess(res) {
            try {
              onAuthenticateSuccess?.(ensureAuthResponse(res));
            } catch {
              setError('unregisteredError', { message: GENERIC_ERROR_MESSAGE });
              onError?.(new Error(GENERIC_ERROR_MESSAGE));
            }
          },
        },
      );
    },
    [clearErrors, mutate, onAuthenticateSuccess, onError, onSubmit, setError],
  );

  return {
    formState,
    hasInputError,
    register,
    isLoading,
    onSubmit: handleSubmit(submitForm, onError),
    validationState,
  };
}
